import { Css, ScrollableParent } from "@homebound/beam";
import { Navigate, Route, Routes } from "react-router-dom";
import { useMatchAny } from "src/hooks/useMatchAny";
import { NotFound } from "src/routes/NotFound";
import { GlobalOptionsPage } from "src/routes/libraries/global-options/GlobalOptionsPage";
import { PlanPackagePage } from "src/routes/libraries/plan-package/details/PlanPackagePage";
import { ProductOfferingConfigsPage } from "src/routes/libraries/product-offering-configs/ProductOfferingConfigsPage";
import { ScheduleTemplateDetailPage } from "src/routes/libraries/schedules/ScheduleTemplateDetail";
import { ScheduleTemplatesPage } from "src/routes/libraries/schedules/ScheduleTemplatesPage";
import { ProductOfferingCostCalculatorPage } from "../developments/product-offerings/ProductOfferingCostCalculatorPage";
import { ProductOfferingScopePage } from "../developments/product-offerings/ProductOfferingScopePage";
import {
  bidItemCreatePath,
  bidItemDetailsPath,
  catalogTaskFolderPath,
  catalogTaskLaborCosts,
  catalogTaskPath,
  catalogTasksPath,
  constraintItemsCatalogPath,
  designCatalogPath,
  designCatalogsPath,
  designPackageCompareModePath,
  designPackageEditOptionPath,
  designPackageFinishSchedulePath,
  designPackagePersonalizationGroupsPath,
  designPackageRequiredOptionsPath,
  designPackageVersionHistoryPath,
  globalOptionPath,
  globalOptionsLegacyPath,
  globalOptionsPath,
  globalProductOfferingConfigsPath,
  globalProductOfferingsPath,
  installTasksPath,
  itemCatalogPath,
  itemTemplatePath,
  itemTemplatesPath,
  materialCostPath,
  materialCreatePath,
  materialDetailsPath,
  materialsCatalogPath,
  milestoneCatalogPath,
  milestonesCatalogPath,
  planPackageEditPath,
  planPackageIncomingSlotReviewPath,
  planPackagePath,
  planPackageTakeoffsPath,
  planPackageVersionHistoryPath,
  planPackagesPath,
  productCatalogPath,
  productCreatePath,
  productDetailsPath,
  productOfferingConfigEditPath,
  productOfferingConfigPath,
  productOfferingConfigPricingTracePath,
  productOfferingCostCalculatorPath,
  productOfferingCreatePath,
  productOfferingEditPath,
  productOfferingScopePath,
  relativeLibrariesPath,
  scheduleTemplatePath,
  scheduleTemplatesPath,
} from "../routesDef";
import { ItemTemplatePage } from "../settings/itemTemplates/ItemTemplatePage";
import { ItemTemplatesPage } from "../settings/itemTemplates/ItemTemplatesPage";
import { DesignCatalogsPage } from "./design-catalog/DesignCatalogsPage";
import { EditPersonalizationOptionPage } from "./design-catalog/EditPersonalizationOptionPage";
import { DesignPackageCompareModePage } from "./design-catalog/compare-mode/DesignPackageCompareMode";
import { DesignPackageConfiguratorLayout } from "./design-catalog/design-package-configurator/DesignPackageConfiguratorLayout";
import { DesignPackageRequiredOptionsPage } from "./design-catalog/design-package-configurator/components/DesignPackageRequiredOptionsPage";
import { FinishSchedulePage } from "./design-catalog/finish-schedules/FinishSchedulePage";
import { DesignPackagePersonalizationGroupsPage } from "./design-catalog/personalization-groups/DesignPackagePersonalizationGroupsPage";
import { DesignPackageVersionHistoryPage } from "./design-catalog/version-history/DesignPackageVersionHistoryPage";
import { GlobalOptionForm } from "./global-options-v2/GlobalOptionForm";
import { GlobalOptionsPageV2 } from "./global-options-v2/GlobalOptionsPageV2";
import { BidItemPage } from "./item-catalog/BidItemPage";
import { ItemCatalogPage } from "./item-catalog/ItemCatalogPage";
import { MaterialCatalogPage } from "./material-catalog/MaterialCatalogPage";
import { MaterialCostPage } from "./material-catalog/MaterialCostPage";
import { MaterialPage } from "./material-catalog/MaterialPage";
import { InstallTasksPage } from "./material-catalog/install-tasks/InstallTasksPage";
import { MilestoneCatalogForm } from "./milestone-catalog/MilestoneCatalogForm";
import { MilestoneCatalogPage } from "./milestone-catalog/MilestoneCatalogPage";
import { PlanPackagesPage } from "./plan-package/list/PlanPackagesPage";
import { CreateOrUpdatePlanPackageWizard } from "./plan-package/stepper";
import { PlanPackageTakeoffPage } from "./plan-package/takeoffs/PlanPackageTakeoffPage";
import { ReviewSlotRequestsPage } from "./plan-package/takeoffs/components/SlotReview/ReviewSlotRequestsPage";
import { PlanPackageVersionHistoryPage } from "./plan-package/version-history/PlanPackageVersionHistoryPage";
import { ProductCatalogPage } from "./product-catalog/ProductCatalogPage";
import { ProductPage } from "./product-catalog/ProductPage";
import { AddProductOfferingConfigPage } from "./product-offering-configs/AddProductOfferingConfigPage";
import { GlobalProductOfferings } from "./product-offerings/GlobalProductOfferings";
import { AddProductOfferingPage } from "./product-offerings/add-product-offering/AddProductOfferingPage";
import { CheckoutPricingTracePage } from "./product-offerings/checkout-pricing-trace/CheckoutPricingTracePage";
import { EditProductOfferingPage } from "./product-offerings/edit-product-offering/EditProductOfferingPage";
import { ConstraintItemCatalogPage } from "./task-catalog/ConstraintItemCatalogPage";
import { TaskCatalogLaborCostsPage } from "./task-catalog/TaskCatalogLaborCostsPage";
import { TaskCatalogPage } from "./task-catalog/TaskCatalogPage";
import { TaskCatalogForm } from "./task-catalog/components/TaskCatalogForm";

export function LibrariesLayout() {
  const hasOwnLayout = !!useMatchAny([
    planPackageEditPath,
    planPackageIncomingSlotReviewPath,
    planPackageTakeoffsPath,
    productOfferingScopePath,
  ]);

  return hasOwnLayout ? (
    <LibraryRouter />
  ) : (
    <ScrollableParent px={3} xss={Css.fg1.bgGray100.$}>
      <LibraryRouter />
    </ScrollableParent>
  );
}

function LibraryRouter() {
  const rl = relativeLibrariesPath;

  return (
    <Routes>
      <Route path={rl(globalOptionsLegacyPath)} element={<GlobalOptionsPage />} />
      <Route path={rl(globalOptionsPath)} element={<GlobalOptionsPageV2 />} />
      <Route path={rl(globalOptionPath)} element={<GlobalOptionForm />} />
      <Route path={rl(productCatalogPath)} element={<ProductCatalogPage />} />
      <Route path={rl(productDetailsPath)} element={<ProductCatalogPage />} />
      <Route path={rl(materialCreatePath)} element={<MaterialPage />} />
      <Route path={rl(materialCostPath)} element={<MaterialCostPage />} />
      <Route path={rl(materialDetailsPath)} element={<MaterialCatalogPage />} />
      <Route path={rl(installTasksPath)} element={<InstallTasksPage />} />
      <Route path={rl(materialsCatalogPath)} element={<MaterialCatalogPage />} />
      <Route path={rl(productCreatePath)} element={<ProductPage />} />
      <Route path={rl(itemCatalogPath)} element={<ItemCatalogPage />} />
      <Route path={rl(bidItemCreatePath)} element={<BidItemPage />} />
      <Route path={rl(bidItemDetailsPath)} element={<ItemCatalogPage />} />
      <Route path={rl(itemTemplatePath)} element={<ItemTemplatePage />} />
      <Route path={rl(scheduleTemplatePath.scheduleTemplate)} element={<ScheduleTemplateDetailPage />} />
      <Route path={rl(scheduleTemplatesPath)} element={<ScheduleTemplatesPage documentTitle="Schedule Templates" />} />
      <Route path={rl(itemTemplatesPath)} element={<ItemTemplatesPage />} />
      <Route path={rl(catalogTasksPath)} element={<TaskCatalogPage />} />
      <Route path={rl(catalogTaskPath)} element={<TaskCatalogForm />} />
      <Route path={rl(catalogTaskLaborCosts)} element={<TaskCatalogLaborCostsPage />} />
      <Route path={rl(constraintItemsCatalogPath)} element={<ConstraintItemCatalogPage />} />
      <Route path={rl(designCatalogsPath)} element={<DesignCatalogsPage />} />
      <Route path={rl(designPackageVersionHistoryPath)} element={<DesignPackageVersionHistoryPage />} />
      <Route path={rl(designPackagePersonalizationGroupsPath)} element={<DesignPackagePersonalizationGroupsPage />} />
      <Route path={rl(designPackageFinishSchedulePath)} element={<FinishSchedulePage />} />
      <Route path={rl(designPackageCompareModePath)} element={<DesignPackageCompareModePage />} />
      <Route path={rl(designPackageEditOptionPath)} element={<EditPersonalizationOptionPage />} />
      <Route path={rl(designPackageRequiredOptionsPath)} element={<DesignPackageRequiredOptionsPage />} />
      <Route path={`${rl(designCatalogPath)}/*`} element={<DesignPackageConfiguratorLayout />} />
      <Route path={rl(catalogTaskFolderPath)} element={<TaskCatalogPage />} />
      <Route path={rl(globalProductOfferingsPath)} element={<GlobalProductOfferings />} />
      <Route path={rl(productOfferingCreatePath)} element={<AddProductOfferingPage />} />
      <Route path={rl(productOfferingEditPath)} element={<EditProductOfferingPage />} />
      <Route path={rl(globalProductOfferingConfigsPath)} element={<ProductOfferingConfigsPage />} />
      <Route path={rl(productOfferingConfigPath)} element={<AddProductOfferingConfigPage />} />
      <Route path={rl(productOfferingConfigEditPath)} element={<AddProductOfferingConfigPage />} />
      <Route path={rl(productOfferingScopePath)} element={<ProductOfferingScopePage />} />
      <Route path={rl(productOfferingCostCalculatorPath)} element={<ProductOfferingCostCalculatorPage />} />
      <Route path={rl(productOfferingConfigPricingTracePath)} element={<CheckoutPricingTracePage />} />
      <Route path={rl(planPackageVersionHistoryPath)} element={<PlanPackageVersionHistoryPage />} />
      <Route path={rl(planPackageIncomingSlotReviewPath)} element={<ReviewSlotRequestsPage />} />
      <Route path={rl(planPackageTakeoffsPath)} element={<PlanPackageTakeoffPage />} />
      <Route path={rl(planPackageEditPath)} element={<CreateOrUpdatePlanPackageWizard />} />
      <Route path={rl(planPackagesPath)} element={<PlanPackagesPage />} />
      <Route path={rl(planPackagePath)} element={<PlanPackagePage />} />
      <Route path={rl(milestonesCatalogPath)} element={<MilestoneCatalogPage />} />
      <Route path={rl(milestoneCatalogPath)} element={<MilestoneCatalogForm />} />
      <Route path="" element={<Navigate to={rl(globalOptionsPath)} replace />} />
      {/* Otherwise, not found */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
